import type { Tool, ToolContext } from '@/modules/Editor/managers/toolManager'
import { setupMouseButtonLoadout } from '@/modules/Editor/plugins/mixins/loadouts'
import { EditorCursor, selectCursor } from '@/modules/Editor/editorCursor'
import { setupToolMouseCallbacks } from '@/modules/Editor/plugins/mixins/setupToolMouseCallbacks'
import type { PointerEvent } from '@/core/utils/touch'
import { resolveEventPoint } from '@/core/utils/touch'
import type { IPoint } from '@/modules/Editor/point'
import { getTopLeftOfScaledPixel } from '@/modules/Editor/plugins/brush/utils/getTopLeftOfScaledPixel'
import { getVoxelRasterDimensions } from '@/modules/Editor/plugins/brush/utils/getVoxelRasterDimensions'
import { isDicomView } from '@/modules/Editor/utils/radiology/isDicomView'
import { createMaskAnnotationIn3D } from '@/modules/Editor/plugins/mask/utils/shared/createMaskAnnotationIn3D'
import { assertVoxelRaster } from '@/modules/Editor/models/raster/assertVoxelRaster'
import { getOrCreateRasterForView } from '@/modules/Editor/plugins/mask/utils/shared/getOrCreateRasterForView'
import { preselectOrPromptForAnnotationClass } from '@/modules/Editor/utils/preselectOrPromptForAnnotationClass'
import type { ToolEvent } from '@/modules/Editor/tools/types'
import { MaskBrushDimension, ToolName } from '@/modules/Editor/tools/types'
import { maskAnnotationType } from '@/modules/Editor/plugins/brush/consts'
import { getPrimaryViewFromView } from '@/modules/Editor/plugins/mask/utils/shared/getPrimaryViewFromView'
import { updateMaskAnnotationIn3D } from '@/modules/Editor/plugins/mask/utils/shared/updateMaskAnnotationIn3D'
import { getAnnotationForClassIdOnRaster } from '@/modules/Editor/plugins/mask/utils/shared/getAnnotationForClassIdOnRaster'
import { isMaskAnnotation } from '@/modules/Editor/models/annotation/annotationKindValidator'
import { getRasterPlaneFromRadiologySlotName } from '@/modules/Editor/utils/raster/getRasterPlaneFromRadiologySlotName'
import { assert } from '@/core/utils/assert'
import type { MaskChangeFunction } from '@/modules/Editor/plugins/mask/utils/shared/applyRasterChange'
import { applyRasterChange } from '@/modules/Editor/plugins/mask/utils/shared/applyRasterChange'
import { Plane } from '@/modules/Editor/utils/raster/Plane'
import { RasterWorker } from '@/modules/Editor/workers/RasterWorker/RasterWorker'
import { ToolEvents } from '@/modules/Editor/eventBus'
import { DSFError } from '@/modules/Editor/plugins/fill/runDFSScan'

export const DEFAULT_TOLERANCE = 30

const getCurrentPoint = (context: ToolContext, event: PointerEvent): IPoint | null => {
  const canvasPoint = resolveEventPoint(event)
  if (!canvasPoint) {
    return null
  }
  return context.editor.activeView.camera.canvasViewToImageView(canvasPoint)
}

const getVoxelCoordinatesForPlaneZ = (point: IPoint, plane: Plane, planeZ: number): IPoint => {
  if (plane === Plane.Y) {
    return {
      x: point.x,
      y: planeZ,
    }
  }

  if (plane === Plane.X) {
    return {
      x: planeZ,
      y: point.x,
    }
  }

  return point
}

const getErrorMessage = (error: DSFError): string => {
  switch (error) {
    case DSFError.STACK_SIZE_EXCEEDED:
      return 'Fill tolerance is too high and could not get a result.\nPlease reduce the tolerance and try again.'
    default:
      return 'An error occurred while filling the volume'
  }
}

export class FillTool implements Tool {
  private dimension: MaskBrushDimension = MaskBrushDimension.Paint3D
  private tolerance: number = DEFAULT_TOLERANCE
  private erasing: boolean = false
  private processing: boolean = false
  private event: ToolEvent
  private activating = false

  rasterWorker: RasterWorker

  constructor() {
    this.rasterWorker = new RasterWorker()
    this.event = {
      toolName: ToolName.Fill,
      lastHotkeyPressed: null,
      editing: false,
    }
  }

  setProcessing(value: boolean): void {
    this.processing = value
    this.updateCursor()
  }

  updateCursor(): void {
    if (this.processing) {
      selectCursor(EditorCursor.Loading)
      return
    }
    if (this.erasing) {
      selectCursor(EditorCursor.Unfill)
      return
    }
    selectCursor(EditorCursor.Fill)
  }

  /**
   * Given the start point, it will call the worker to get all the points to fill, fill them
   * and then save the raster.
   */
  async applyAndSave(
    context: ToolContext,
    classId: number,
    voxelCoordinate: IPoint,
    plane: Plane,
  ): Promise<void> {
    const dicomView = getPrimaryViewFromView(context.editor.activeView)
    if (!dicomView || !isDicomView(dicomView)) {
      throw new Error('Active view is not a DicomView')
    }

    const raster = getOrCreateRasterForView(dicomView)
    const voxelRaster = assertVoxelRaster(raster)

    if (!classId) {
      throw new Error('No class selected')
    }

    const existingLabelIndex = raster.getLabelIndexByClassId(classId)
    const labelIndex = this.erasing ? 0 : existingLabelIndex || raster.getNextAvailableLabelIndex()
    const { x, y } = voxelCoordinate

    const voxelValue = dicomView.getVoxelValueAt(x, y, dicomView.currentFrameIndex, plane)
    const initialBounds = voxelRaster.getVideoBoundsForLabelIndexForFrame(
      labelIndex,
      dicomView.currentFrameIndex,
    ) || {
      topLeft: { x, y },
      bottomRight: { x: x + 1, y: y + 1 },
    }

    this.setProcessing(true)

    performance.mark('start-flood-fill')
    if (!this.rasterWorker.hasDicomVoxelBuffer()) {
      const dicomVoxelBuffer = dicomView.vtkDataManager?.getSourceDataBuffer()
      if (!dicomVoxelBuffer) {
        throw new Error('DICOM Voxel buffer not found')
      }
      this.rasterWorker.setDicomVoxelBuffer(dicomVoxelBuffer)
    }

    performance.mark('start-DSF')
    const result = await this.rasterWorker.fillFromPointOnRaster({
      x: voxelCoordinate.x,
      y: voxelCoordinate.y,
      z: dicomView.currentFrameIndex,
      w: voxelRaster.width,
      h: voxelRaster.height,
      totalFrames: dicomView.totalFrames,
      initialBounds: JSON.parse(JSON.stringify(initialBounds)),
      plane: plane,
      maxVoxelValue: voxelValue + this.tolerance,
      minVoxelValue: voxelValue - this.tolerance,
      navigateZ: this.dimension === MaskBrushDimension.Paint3D,
    })
    performance.mark('end-DSF')

    if (!result) {
      // operation was probably terminated or cancelled
      ToolEvents.floodFillTerminated.emit(this.event, 'The fill operation was cancelled')
      this.setProcessing(false)
      return
    }

    const { found, bounds: fillingBounds, depth: fillingDepth, error } = result

    if (error === DSFError.STACK_SIZE_EXCEEDED) {
      ToolEvents.floodFillTerminated.emit(this.event, getErrorMessage(error))
      this.setProcessing(false)
      return
    }

    const changedFrames = Object.keys(found).map(Number)

    const depth = fillingDepth.maxZ - fillingDepth.minZ

    // create an apply method to run the raster updates with undo/redo
    const apply = async (): ReturnType<MaskChangeFunction> => {
      // Apply changes to the raster
      const changedLabels = new Set<number>([labelIndex])
      changedFrames.forEach((z: number): void => {
        const buffer = voxelRaster.getBufferForEdit(z).getBuffer()
        found[z].forEach(([x, y]) => {
          const index = y * voxelRaster.width + x
          changedLabels.add(buffer[index])
          buffer[index] = labelIndex
        })
      })

      if (!existingLabelIndex) {
        await createMaskAnnotationIn3D(
          dicomView,
          voxelRaster,
          fillingBounds,
          labelIndex,
          classId,
          depth,
          changedFrames,
        )
      } else {
        const annotation = getAnnotationForClassIdOnRaster(dicomView, voxelRaster, classId)

        if (!annotation || !isMaskAnnotation(annotation)) {
          throw new Error('Mask annotation not found')
        }

        updateMaskAnnotationIn3D(
          dicomView,
          voxelRaster,
          annotation,
          fillingBounds,
          depth,
          changedFrames,
        )
      }

      return { changedLabels: Array.from(changedLabels) }
    }

    await applyRasterChange(apply, voxelRaster, true, changedFrames)

    this.setProcessing(false)

    if (changedFrames.length > 0) {
      voxelRaster.invalidate3D({
        minZ: fillingDepth.minZ,
        maxZ: fillingDepth.maxZ,
        minX: fillingBounds.topLeft.x,
        maxX: fillingBounds.bottomRight.x,
        minY: fillingBounds.topLeft.y,
        maxY: fillingBounds.bottomRight.y,
      })
    }

    performance.mark('end-flood-fill')
  }

  async onEnd(context: ToolContext, event: PointerEvent): Promise<void> {
    if (this.processing) {
      this.terminateAction()
      return
    }
    const currentSelectedPoint = assert(getCurrentPoint(context, event))

    const classId = assert(
      context.editor.activeView.editor.preselectedAnnotationClassId,
      'Fill tool needs a class id',
    )
    const file = context.editor.activeView.fileManager.file

    const { metadata, slot_name } = file
    const plane = assert(
      getRasterPlaneFromRadiologySlotName(slot_name, metadata),
      'Could not get the plane from the slot name',
    )
    const { voxelCoordinate } = getTopLeftOfScaledPixel(
      currentSelectedPoint,
      metadata,
      getVoxelRasterDimensions(context.editor.activeView),
    )

    // translate the clicked point to get the coordinates on the Z plane (where the voxel raster is)
    const voxelCoordinateForPlaneZ = getVoxelCoordinatesForPlaneZ(
      voxelCoordinate,
      plane,
      context.editor.activeView.currentFrameIndex,
    )

    await this.applyAndSave(context, classId, voxelCoordinateForPlaneZ, Plane.Z)

    performance.measure('DSF run duration', 'start-DSF', 'end-DSF')
    performance.measure('Flood fill duration', 'start-flood-fill', 'end-flood-fill')
  }

  terminateAction(): void {
    this.processing = false
    this.rasterWorker.cleanup()
    this.rasterWorker = new RasterWorker()
    this.updateCursor()
  }

  public async activate(context: ToolContext): Promise<void> {
    this.activating = true
    setupMouseButtonLoadout(context, { middle: true })

    const classSelected = await preselectOrPromptForAnnotationClass(
      context.editor.activeView,
      ToolName.Fill,
      [maskAnnotationType],
      'You must create or select an existing Mask class before using the fill tool',
    )

    if (!classSelected || !this.activating) {
      this.deactivate(context)
      return
    }

    this.rasterWorker = new RasterWorker()
    selectCursor(EditorCursor.Fill)

    setupToolMouseCallbacks(
      context,
      () => {},
      () => {},
      this.onEnd.bind(this),
    )

    context.editor.registerCommand(
      'fill_tool.change_dimension',
      (_: KeyboardEvent, dimension?: MaskBrushDimension): void => {
        this.dimension = dimension
          ? dimension
          : this.dimension === MaskBrushDimension.Paint2D
            ? MaskBrushDimension.Paint3D
            : MaskBrushDimension.Paint2D

        ToolEvents.floodFillDimensionChange.emit(
          {
            toolName: ToolName.Fill,
            lastHotkeyPressed: null,
            editing: false,
          },
          this.dimension,
        )
      },
    )

    context.editor.registerCommand('fill_tool.change_tolerance', (tolerance: number): void => {
      this.tolerance = tolerance
    })

    context.editor.registerCommand('fill_tool.change_mode_to_remove', (): void => {
      this.erasing = true
      this.updateCursor()
    })

    context.editor.registerCommand('fill_tool.change_mode_to_add', (): void => {
      this.erasing = false
      this.updateCursor()
    })
  }

  public deactivate(context: ToolContext): void {
    this.activating = false
    this.erasing = false

    context.editor.unregisterCommand('fill_tool.change_dimension')
    context.editor.unregisterCommand('fill_tool.change_tolerance')
    context.handles.forEach((handle) => handle.release())
    context.handles.length = 0
    this.rasterWorker.cleanup()

    if (this.processing) {
      ToolEvents.floodFillTerminated.emit(this.event, 'Fill operation was cancelled')
      this.processing = false
    }
  }

  save(): void {}
  reset(): void {}
}

export const fillTool = new FillTool()
